'use client';

import { createContext, useContext, type ComponentType, type ReactNode } from 'react';

/** A custom datum baked onto a message by the host, rendered after the bubble by
 *  the host component registered under the same `name`. */
export interface MessageDataPart {
  name: string;
  data: unknown;
}

export function isMessageDataPart(part: unknown): part is MessageDataPart {
  return (
    typeof part === 'object' && part !== null && typeof (part as MessageDataPart).name === 'string'
  );
}

export type MessageDataComponentProps<T = unknown> = { data: T };

/** Host-provided components keyed by data-part name. The host bakes
 *  `{ name, data }` parts onto messages (so they load with the message and the
 *  read-only gate covers them) and registers a component per name to render them. */
export type MessageDataComponents = Record<string, ComponentType<MessageDataComponentProps<never>>>;

const MessageDataComponentsContext = createContext<MessageDataComponents>({});

export function MessageDataComponentsProvider({
  children,
  dataComponents,
}: {
  children: ReactNode;
  dataComponents?: MessageDataComponents;
}) {
  return (
    <MessageDataComponentsContext.Provider value={dataComponents ?? {}}>
      {children}
    </MessageDataComponentsContext.Provider>
  );
}

export function useMessageDataComponents(): MessageDataComponents {
  return useContext(MessageDataComponentsContext);
}
